import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { ChevronLeft, ChevronRight, Loader2, CheckCircle2, AlertCircle, Clock, Car, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError } from "@/components/einsatz/felder";
import { MinijobWarnung } from "@/components/crm/controlling/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONAT_NAMEN = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

function getMonatStr(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function parseMonat(s) { const [y, m] = s.split("-").map(Number); return new Date(y, m - 1, 1); }
function formatMonat(s) { const d = parseMonat(s); return `${MONAT_NAMEN[d.getMonth()]} ${d.getFullYear()}`; }
function prevMonat(s) { const d = parseMonat(s); d.setMonth(d.getMonth() - 1); return getMonatStr(d); }
function nextMonat(s) { const d = parseMonat(s); d.setMonth(d.getMonth() + 1); return getMonatStr(d); }

function AuslastungBar({ soll, kunden }) {
  if (!soll || soll === 0) return <span className="text-xs text-slate-400">—</span>;
  const pct = kunden != null ? Math.round((kunden / soll) * 100) : 0;
  const color = pct >= 95 ? "bg-green-500" : pct >= 75 ? "bg-cyan-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  const textColor = pct >= 95 ? "text-green-700" : pct >= 75 ? "text-cyan-700" : pct >= 50 ? "text-amber-700" : "text-red-700";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className={`text-sm font-bold ${textColor} min-w-[3rem] text-right`}>{kunden != null ? `${pct}%` : "—"}</span>
    </div>
  );
}

function formatDT(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function KalenderStatus({ entry }) {
  if (!entry.kalender_kontrolliert) return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400"><Clock className="h-3 w-3" />Offen</span>;
  if (entry.termine_vollstaendig) return <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600"><CheckCircle2 className="h-3 w-3" />Erledigt</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><AlertCircle className="h-3 w-3" />Nacharbeit</span>;
}

export default function CRMControlling({ monat: monatProp, gesperrt = false, onChanged }) {
  const { user } = useAuth();
  const [monatState, setMonat] = useState(getMonatStr(new Date()));
  const monat = monatProp || monatState;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const headers = { Authorization: `Bearer ${user?.token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/controlling/${monat}`, { headers });
      setData(res.data);
    } catch { toast.error("Fehler beim Laden."); }
    setLoading(false);
  }, [monat]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateEntry = async (mid, updates) => {
    setSavingId(mid);
    try {
      await axios.patch(`${API}/controlling/${monat}/${mid}`, updates, { headers });
      await fetchData();
      onChanged?.();
      toast.success("Gespeichert");
    } catch { toast.error("Fehler beim Speichern."); }
    setSavingId(null);
  };

  const [sendingReminder, setSendingReminder] = useState(false);

  const sendReminder = async () => {
    setSendingReminder(true);
    try {
      const res = await axios.post(`${API}/controlling/check-reminders`, {}, { headers });
      if (res.data.reminders_sent) {
        toast.success(`Erinnerung gesendet: ${res.data.offene_kalender || 0} Kalender, ${res.data.offene_stunden || 0} Stunden offen`);
      } else {
        toast.info(res.data.reason || "Alle Kontrollen erledigt — keine Erinnerung nötig");
      }
    } catch { toast.error("Fehler beim Senden."); }
    setSendingReminder(false);
  };

  const kalenderDone = data.filter(d => d.kalender_kontrolliert).length;
  const stundenDone = data.filter(d => d.stundenkontrolle_abgeschlossen).length;
  const total = data.length;

  // Check if reminders are due
  const today = new Date().getDate();
  const kalenderOverdue = today > 6 && kalenderDone < total && total > 0;
  const stundenOverdue = today > 30 && stundenDone < total && total > 0;

  return (
    <div data-testid="crm-controlling">
      {/* Overdue Banner */}
      {(kalenderOverdue || stundenOverdue) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="overdue-banner">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
            <p className="text-sm font-medium text-amber-800">
              {kalenderOverdue && `${total - kalenderDone} Kalenderkontrolle(n) überfällig. `}
              {stundenOverdue && `${total - stundenDone} Stundenkontrolle(n) überfällig.`}
            </p>
          </div>
          <Button size="sm" onClick={sendReminder} disabled={sendingReminder} variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-100 rounded-lg text-xs whitespace-nowrap" data-testid="send-reminder-btn">
            {sendingReminder ? "Sende..." : "WhatsApp-Erinnerung senden"}
          </Button>
        </div>
      )}

      {/* Header */}
      {!monatProp && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">Mitarbeiter-Controlling</h1>
          <p className="text-sm text-slate-500 mt-1">Monatliche Kontrolle und Auslastung</p>
        </div>
      </div>}

      {/* Month navigator */}
      {!monatProp && <div className="flex items-center justify-center gap-4 mb-6 bg-white rounded-xl border border-slate-200 p-3" data-testid="month-nav">
        <button onClick={() => setMonat(prevMonat(monat))} className="p-2 rounded-lg hover:bg-slate-100 transition-all" data-testid="month-prev">
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </button>
        <h2 className="font-['Manrope'] text-lg sm:text-xl font-bold text-slate-900 min-w-[200px] text-center" data-testid="month-label">
          {formatMonat(monat)}
        </h2>
        <button onClick={() => setMonat(nextMonat(monat))} className="p-2 rounded-lg hover:bg-slate-100 transition-all" data-testid="month-next">
          <ChevronRight className="h-5 w-5 text-slate-600" />
        </button>
      </div>}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{total}</p>
          <p className="text-xs text-slate-500">Mitarbeiter</p>
        </div>
        <div className={`rounded-xl border p-4 text-center ${kalenderDone === total && total > 0 ? "bg-green-50 border-green-200" : "bg-white border-slate-200"}`}>
          <p className="text-2xl font-bold text-slate-900">{kalenderDone}/{total}</p>
          <p className="text-xs text-slate-500">Kalenderkontrolle</p>
        </div>
        <div className={`rounded-xl border p-4 text-center ${stundenDone === total && total > 0 ? "bg-green-50 border-green-200" : "bg-white border-slate-200"}`}>
          <p className="text-2xl font-bold text-slate-900">{stundenDone}/{total}</p>
          <p className="text-xs text-slate-500">Stundenkontrolle</p>
        </div>
        <div className={`rounded-xl border p-4 text-center ${(total - kalenderDone - (total - stundenDone)) <= 0 && total > 0 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
          <p className="text-2xl font-bold text-slate-900">{(total - kalenderDone) + (total - stundenDone)}</p>
          <p className="text-xs text-slate-500">Offene Aufgaben</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>
      ) : data.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Keine aktiven Mitarbeiter für diesen Monat.</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Mitarbeiter</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-700">Kalender</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-700">Soll</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-700">Kunden-h</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-700">Ist-h</th>
                  <th className="text-right py-3 px-3 font-semibold text-slate-700">Differenz</th>
                  <th className="py-3 px-3 font-semibold text-slate-700 min-w-[140px]">Auslastung</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-700">Std.-Kontrolle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.map(entry => {
                  const diff = entry.kundenstunden != null ? entry.kundenstunden - entry.sollstunden : null;
                  return (
                    <tr key={entry.mitarbeiter_id} data-testid={`ctrl-row-${entry.mitarbeiter_id}`} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setExpandedId(expandedId === entry.mitarbeiter_id ? null : entry.mitarbeiter_id)}>
                      <td className="py-3 px-4 font-medium text-slate-900">{entry.vorname} {entry.nachname}{entry.vorbereitung_abgeschlossen && <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid={`ctrl-vorbereitet-${entry.mitarbeiter_id}`}>vorbereitet</span>}{entry.minijob_warnung && <span className="ml-2"><MinijobWarnung w={entry.minijob_warnung} klein /></span>}</td>
                      <td className="py-3 px-3 text-center"><KalenderStatus entry={entry} /></td>
                      <td className="py-3 px-3 text-right text-slate-600">{entry.beschaeftigungsart === "Minijob" ? <span title="Minijob: keine Sollstunden">–</span> : `${entry.sollstunden} Std.`}</td>
                      <td className="py-3 px-3 text-right font-medium">{entry.kundenstunden != null ? `${entry.kundenstunden} Std.` : "—"}</td>
                      <td className="py-3 px-3 text-right font-medium">{entry.ist_stunden != null ? `${entry.ist_stunden} Std.` : "—"}</td>
                      <td className={`py-3 px-3 text-right font-bold ${diff != null ? (diff >= 0 ? "text-green-600" : "text-red-600") : "text-slate-400"}`}>
                        {diff != null ? `${diff > 0 ? "+" : ""}${diff} Std.` : "—"}
                      </td>
                      <td className="py-3 px-3"><AuslastungBar soll={entry.sollstunden} kunden={entry.kundenstunden} /></td>
                      <td className="py-3 px-3 text-center">
                        {entry.stundenkontrolle_abgeschlossen
                          ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600"><CheckCircle2 className="h-3 w-3" />Erledigt</span>
                          : <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400"><Clock className="h-3 w-3" />Offen</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3 mb-6">
            {data.map(entry => {
              const diff = entry.kundenstunden != null ? entry.kundenstunden - entry.sollstunden : null;
              const isExpanded = expandedId === entry.mitarbeiter_id;
              return (
                <div key={entry.mitarbeiter_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`ctrl-card-${entry.mitarbeiter_id}`}>
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : entry.mitarbeiter_id)}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-slate-900">{entry.vorname} {entry.nachname}{entry.vorbereitung_abgeschlossen && <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">vorbereitet</span>}</p>{entry.minijob_warnung && <div className="mt-1"><MinijobWarnung w={entry.minijob_warnung} klein /></div>}
                      <div className="flex items-center gap-2">
                        <KalenderStatus entry={entry} />
                        {entry.stundenkontrolle_abgeschlossen && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                      </div>
                    </div>
                    <AuslastungBar soll={entry.sollstunden} kunden={entry.kundenstunden} />
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>Soll: {entry.beschaeftigungsart === "Minijob" ? "– (Minijob)" : `${entry.sollstunden} Std.`}</span>
                      <span>Kunden: {entry.kundenstunden != null ? `${entry.kundenstunden} Std.` : "—"}</span>
                      <span>Ist: {entry.ist_stunden != null ? `${entry.ist_stunden} Std.` : "—"}</span>
                      {diff != null && <span className={diff >= 0 ? "text-green-600 font-bold" : "text-red-600 font-bold"}>{diff > 0 ? "+" : ""}{diff}h</span>}
                    </div>
                  </div>
                  {isExpanded && <ExpandedControl entry={entry} monat={monat} headers={headers} onSave={() => { fetchData(); onChanged?.(); }} savingId={savingId} setSavingId={setSavingId} gesperrt={gesperrt} />}
                </div>
              );
            })}
          </div>

          {/* Desktop expanded row below table */}
          {expandedId && (
            <div className="hidden lg:block">
              {data.filter(e => e.mitarbeiter_id === expandedId).map(entry => (
                <ExpandedControl key={entry.mitarbeiter_id} entry={entry} monat={monat} headers={headers} onSave={() => { fetchData(); onChanged?.(); }} savingId={savingId} setSavingId={setSavingId} gesperrt={gesperrt} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ExpandedControl({ entry, monat, headers, onSave, savingId, setSavingId, gesperrt: bereichGesperrt }) {
  const { user } = useAuth();
  const gesperrt = bereichGesperrt || !!entry.vorbereitung_abgeschlossen;
  const wiederOeffnen = async () => {
    if (!window.confirm(`Vorbereitung für ${entry.vorname} ${entry.nachname} wieder öffnen? Teil 2 wird für diese Person wieder gesperrt.`)) return;
    try { await axios.post(`${API}/auswertung/monat/${monat}/vorbereitung-oeffnen?mitarbeiter_id=${entry.mitarbeiter_id}`, {}, { headers }); toast.success("Vorbereitung wieder geöffnet"); onSave(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Fehler"); }
  };
  const [kalKontrolliert, setKalKontrolliert] = useState(entry.kalender_kontrolliert || false);
  const [termVollst, setTermVollst] = useState(entry.termine_vollstaendig || false);
  const [kalBem, setKalBem] = useState(entry.kalender_bemerkung || "");
  const [kundenstd, setKundenstd] = useState(entry.kundenstunden ?? "");
  const [istStd, setIstStd] = useState(entry.ist_stunden ?? "");
  const [stdBem, setStdBem] = useState(entry.stunden_bemerkung || "");
  const [stdDone, setStdDone] = useState(entry.stundenkontrolle_abgeschlossen || false);
  const isSaving = savingId === entry.mitarbeiter_id;

  const saveKalender = async () => {
    setSavingId(entry.mitarbeiter_id);
    try {
      await axios.patch(`${API}/controlling/${monat}/${entry.mitarbeiter_id}`, {
        kalender_kontrolliert: kalKontrolliert,
        termine_vollstaendig: termVollst,
        kalender_bemerkung: kalBem,
      }, { headers });
      toast.success("Kalenderkontrolle gespeichert");
      onSave();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Fehler"); }
    setSavingId(null);
  };

  const saveStunden = async () => {
    setSavingId(entry.mitarbeiter_id);
    try {
      await axios.patch(`${API}/controlling/${monat}/${entry.mitarbeiter_id}`, {
        kundenstunden: kundenstd === "" ? null : parseFloat(kundenstd),
        ist_stunden: istStd === "" ? null : parseFloat(istStd),
        stunden_bemerkung: stdBem,
        stundenkontrolle_abgeschlossen: stdDone,
      }, { headers });
      toast.success("Stundenkontrolle gespeichert");
      onSave();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Fehler"); }
    setSavingId(null);
  };

  return (
    <div className="border-t border-slate-200 bg-slate-50 p-5" data-testid={`ctrl-expand-${entry.mitarbeiter_id}`}>
      <p className="font-['Manrope'] font-bold text-slate-900 mb-4">{entry.vorname} {entry.nachname} — {formatMonat(monat)}</p>

      {entry.minijob_warnung && <div className={`rounded-lg px-3 py-2 mb-3 text-xs font-semibold ${entry.minijob_warnung.stufe === "ueberschritten" || entry.minijob_warnung.stufe === "prognose" ? "bg-red-50 border border-red-200 text-red-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`} data-testid="ctrl-minijob-warnung">{entry.minijob_warnung.text}</div>}
      {gesperrt && <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3" data-testid="ctrl-gesperrt"><span className="flex-1">Vorbereitung abgeschlossen{entry.vorbereitung_am ? ` am ${formatDT(entry.vorbereitung_am)} von ${entry.vorbereitung_von?.name || ""}` : ""} – Änderungen nur nach Wiederöffnen durch die Geschäftsführung.</span>{user?.role === "admin" && <button onClick={wiederOeffnen} className="h-8 px-3 rounded-lg border border-emerald-300 bg-white text-emerald-800" data-testid={`ctrl-oeffnen-${entry.mitarbeiter_id}`}>Wieder öffnen</button>}</div>}
      <fieldset disabled={gesperrt} className="grid md:grid-cols-2 gap-6 disabled:opacity-60">
        {/* Routine 1: Kalenderkontrolle */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-bold text-cyan-700">1</span>
            Kalenderkontrolle
          </h3>
          <label className="flex items-center gap-2 mb-2 cursor-pointer">
            <input type="checkbox" checked={kalKontrolliert} onChange={e => setKalKontrolliert(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-cyan-700 focus:ring-cyan-500" />
            <span className="text-sm text-slate-700">Kalender kontrolliert</span>
          </label>
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input type="checkbox" checked={termVollst} onChange={e => setTermVollst(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500" />
            <span className="text-sm text-slate-700">Termine vollständig / Planung korrekt</span>
          </label>
          <Input value={kalBem} onChange={e => setKalBem(e.target.value)} placeholder="Bemerkung (optional)" className="h-9 text-sm rounded-lg mb-3" />
          <Button size="sm" onClick={saveKalender} disabled={isSaving} className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-lg text-xs w-full">
            {isSaving ? "..." : "Kalenderkontrolle speichern"}
          </Button>
          {entry.kalender_kontrolliert_am && (
            <p className="text-xs text-slate-400 mt-2">Kontrolliert am {formatDT(entry.kalender_kontrolliert_am)} von {entry.kalender_kontrolliert_von}</p>
          )}
        </div>

        {/* Routine 2: Stundenkontrolle */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">2</span>
            Stunden- & Auslastungskontrolle
          </h3>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-xs text-slate-500 block mb-0.5">Sollstunden</label>
              <Input value={entry.beschaeftigungsart === "Minijob" ? "– (Minijob)" : entry.sollstunden} disabled className="h-9 text-sm rounded-lg bg-slate-50" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-0.5">Kundenstunden</label>
              <Input type="number" step="0.5" value={kundenstd} onChange={e => setKundenstd(e.target.value)} placeholder="—" className="h-9 text-sm rounded-lg" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="text-xs text-slate-500 block mb-0.5">Ist-Stunden</label>
              <Input type="number" step="0.5" value={istStd} onChange={e => setIstStd(e.target.value)} placeholder="—" className="h-9 text-sm rounded-lg" />
            </div>
            <p className="text-[11px] text-slate-500 self-end pb-2">Fahrtzeit & Kilometer werden am Monatsende in Teil 2 erfasst.</p>
          </div>
          <Input value={stdBem} onChange={e => setStdBem(e.target.value)} placeholder="Bemerkung (optional)" className="h-9 text-sm rounded-lg mb-2" />
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input type="checkbox" checked={stdDone} onChange={e => setStdDone(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500" />
            <span className="text-sm text-slate-700 font-medium">Stundenkontrolle abgeschlossen</span>
          </label>
          <Button size="sm" onClick={saveStunden} disabled={isSaving} className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs w-full">
            {isSaving ? "..." : "Stundenkontrolle speichern"}
          </Button>
          {entry.stundenkontrolle_am && (
            <p className="text-xs text-slate-400 mt-2">Abgeschlossen am {formatDT(entry.stundenkontrolle_am)} von {entry.stundenkontrolle_von}</p>
          )}
        </div>
      </fieldset>
    </div>
  );
}
